import { AfterViewInit, Component, Inject, Input, EventEmitter, Output } from '@angular/core';

import { Observable, Subject, Subscription, combineLatest } from 'rxjs';

import { UserService } from '../../../../data-domains/humain/user/services/user.service';
import { UserTLR } from '../../../../data-domains/humain/user/entities/user-tlr.entity';

import { AgenceService } from '../services/agence.service';
import { AgenceTLR } from '../entities/agence-tlr.entity';

import { AntenneService } from '../../antenne/services/antenne.service';
import { AntenneTLR } from '../../antenne/entities/antenne-tlr.entity';

@Component({
	selector: 'agence-selector',
	templateUrl: 'agence-selector.html',
	styleUrls: ['agence-selector.css']
})
export class AgenceSelectorComponent implements AfterViewInit {
	// Id du champ
	@Input() id!: string;

	// Mode du champ
	@Input() disabled = false;

	// Parametres de style
	public _mandatory: boolean = false;
	@Input() set mandatory(value: boolean | undefined) {
		if (value) {
			this._mandatory = value;
		}
	}
	get mandatory(): boolean | undefined {
		return this._mandatory;
	}
	public _labelClass = 'col-md-1';
	@Input() set labelClass(value: string | undefined) {
		if (value) {
			this._labelClass = value;
		}
	}
	get labelClass(): string | undefined {
		return this._labelClass;
	}
	public _fieldClass = 'col-md-3';
	@Input() set fieldClass(value: string | undefined) {
		if (value) {
			this._fieldClass = value;
		}
	}
	get fieldClass(): string | undefined {
		return this._fieldClass;
	}

	// Label pour l'option wildcard (dont la valeur vaut 'null'). L'absence de label (undefined) signifie que cette option n'est pas autorisee
	@Input() wildcardOptionLabel: string | undefined = 'Tous';

	// Valeur par defaut
	@Input() set defaultAgenceId(value: number | null | undefined) {
		this.defaultAgenceIdSubject.next(value);
	}
	private defaultAgenceIdSubject = new Subject<number | null | undefined>();
	
	// Agence de l'utilisateur connecte
	private userAgenceIdSubject = new Subject<number | null>();
	
	// Observable pour les signaux de reinitialisation des valeurs par defaut
	private resetSelectionSubscription!: Subscription;
	@Input() set resetCommands(commands: Observable<void> | undefined) {
		if (this.resetSelectionSubscription) {
			this.resetSelectionSubscription.unsubscribe();
		}
		if (commands) {
			this.resetSelectionSubscription = commands.subscribe(
				() => this.resetSelectionCommands.next()
			);
		}
	}
	private resetSelectionCommands = new Subject<void>();
	
	// One-way out binding pour la selection actuelle en format id seul
	selectedAgenceId!: number | null;
	@Output() selectedAgenceIdChange = new EventEmitter<number | null>();

	// One-way out binding pour la selection actuelle en format complet
	@Output() selectedAgenceChange = new EventEmitter<AgenceTLR | null>();

	// Liste des agences autorisees
	agences!: AgenceTLR[];
	agencesLoadedSubject = new Subject<boolean>();

	constructor( //
		@Inject(UserService) private userService: UserService,
		@Inject(AgenceService) private agenceService: AgenceService,
		@Inject(AntenneService) private antenneService: AntenneService
	) {
		// Programmer le calcul de la valeur a placer initialement dans le select une fois qu'aura ete charge tous les elements suivants:
		// - la valeur par defaut suggeree par le parent
		// - la valeur par defaut provenant des donnees de l'utilisateur connecte
		// - la liste des agences
		combineLatest([this.defaultAgenceIdSubject, this.userAgenceIdSubject, this.agencesLoadedSubject, this.resetSelectionCommands]).subscribe(
			(data: [number | null | undefined, number | null, boolean, void]) => {
				let defaultFromParent = data[0];
				let defaultFromUser = data[1];
				if (defaultFromParent === undefined) {
					this.selectedAgenceId = defaultFromUser;
					this.notifyChange();
				} else {
					this.selectedAgenceId = defaultFromParent;
				}
			}
		);
		
		// Lancer le chargement l'ensemble des agences
		this.agenceService.getAgenceTLRList().subscribe(
			(agences: AgenceTLR[]) => {
				this.agences = agences;
				this.agencesLoadedSubject.next(true);
			}
		);

		// Placer comme valeur par defaut initiale 'undefined', au cas ou le parent en fasse aucun binding
		this.defaultAgenceIdSubject.next(undefined);
		this.resetSelectionCommands.next();
		
		// Aller chercher l'agence de l'utilisateur (si elle existe)
		this.userService.getCurrentUserTLR().subscribe(
			(user: UserTLR) => {
				// En fonction du role de l'utilisateur, determiner la valeur par defaut
				const userRole = user.role;
				if (userRole === 'EXP') {
					const defaultAntenneId = user.antenneId;
					
					this.antenneService.getAntenneTLRById(defaultAntenneId).subscribe(
						(antenne: AntenneTLR) => {
							const defaultAgenceId = antenne.agenceId;
							this.userAgenceIdSubject.next(defaultAgenceId);
						}
					);
				} else {
					this.userAgenceIdSubject.next(null);
				}
			}
		);
	}

	ngAfterViewInit() {
	}

	onSelectionChange() {
		this.notifyChange();
	}

	onCancelSelection() {
		if (!this.disabled) {
			this.selectedAgenceId = null;
			this.notifyChange();
		}
	}

	private notifyChange() {
		const id = this.selectedAgenceId;
		this.selectedAgenceIdChange.emit(id);
		if (id) {
			this.agenceService.getAgenceTLRById(id).subscribe(
				(agence: AgenceTLR | undefined) => {
					if (agence) {
						this.selectedAgenceChange.emit(agence);
					} else {
						throw new Error("Server doesn't recognize this agence id anymore: " + id);
					}
				}
			);
		} else {
			this.selectedAgenceChange.emit(null);
		}
	}
}